import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Space, Select, message } from 'antd';
import { UserOutlined, LockOutlined, GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuthStore } from '../store/auth';

const { Title, Text } = Typography;
const { Option } = Select;

interface LoginForm {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: LoginForm) => {
    setLoading(true);
    try {
      const response: any = await api.post('/auth/login', values);
      
      setAuth(response.data.user, response.data.accessToken);
      message.success(t('auth.loginSuccess'));
      
      // Navigate based on role
      const roleCode = response.data.user.role.code;
      if (roleCode === 'admin' || roleCode === 'cost_admin') {
        navigate('/dashboard');
      } else if (roleCode === 'ie') {
        navigate('/ie');
      } else if (roleCode === 'procurement') {
        navigate('/materials');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ marginBottom: 0 }}>
              Amphenol AUTO
            </Title>
            <Text type="secondary">Cost Management System</Text>
          </div>
          
          <Form
            name="login"
            onFinish={onFinish}
            autoComplete="off"
            size="large"
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: t('auth.username') }]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder={t('auth.username')} 
              />
            </Form.Item>
            
            <Form.Item
              name="password"
              rules={[{ required: true, message: t('auth.password') }]}
            >
              <Input.Password 
                prefix={<LockOutlined />} 
                placeholder={t('auth.password')} 
              />
            </Form.Item>
            
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                {t('auth.login')}
              </Button>
            </Form.Item>
          </Form>
          
          <div style={{ textAlign: 'center' }}>
            <Select
              value={i18n.language}
              onChange={changeLanguage}
              style={{ width: 120 }}
              suffixIcon={<GlobalOutlined />}
            >
              <Option value="en">English</Option>
              <Option value="zh">中文</Option>
            </Select>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default Login;